import React, { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { Chip, Tooltip } from '@mui/material';
import { 
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Sync as SyncIcon
} from '@mui/icons-material';

const RealtimeIndicator: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const client = useApolloClient();

  useEffect(() => {
    // Monitor WebSocket connection status
    const checkConnection = () => {
      // Check if the Apollo Client has an active WebSocket connection
      const link = (client as any).link;
      if (link && link.subscriptionClient) {
        const wsClient = link.subscriptionClient;
        
        // Listen for connection events
        wsClient.on('connected', () => {
          setIsConnected(true);
          setIsReconnecting(false);
        });
        
        wsClient.on('disconnected', () => {
          setIsConnected(false);
          setIsReconnecting(false);
        });
        
        wsClient.on('reconnecting', () => {
          setIsReconnecting(true);
        });
        
        // Check initial status
        setIsConnected(wsClient.status === 1); // 1 = OPEN
      } else {
        // Fallback: assume connected if client exists
        setIsConnected(true);
      }
    };

    checkConnection();
    
    // Check connection status periodically
    const interval = setInterval(checkConnection, 5000);
    
    return () => clearInterval(interval);
  }, [client]);

  const getStatusInfo = () => {
    if (isReconnecting) {
      return {
        icon: <SyncIcon fontSize="small" />,
        label: 'Reconnecting...',
        color: 'warning' as const,
        tooltip: 'Attempting to reconnect to real-time updates'
      };
    }
    
    if (isConnected) {
      return {
        icon: <WifiIcon fontSize="small" />,
        label: 'Live',
        color: 'success' as const,
        tooltip: 'Connected - receiving real-time updates'
      };
    }
    
    return {
      icon: <WifiOffIcon fontSize="small" />,
      label: 'Offline',
      color: 'error' as const,
      tooltip: 'Not connected - updates may be delayed'
    };
  };

  const status = getStatusInfo();

  return (
    <Tooltip title={status.tooltip}>
      <Chip
        icon={status.icon}
        label={status.label}
        color={status.color}
        size="small"
        variant="outlined"
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
          animation: isReconnecting ? 'pulse 1.5s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.6 },
            '100%': { opacity: 1 }
          }
        }}
      />
    </Tooltip>
  );
};

export default RealtimeIndicator;
